/**
 * WeekView component for calendar
 * Displays events in a weekly timeline layout
 */

'use client';

import type { Event } from '../types';
import { useEventsComponents } from '../config';
import { darkenHexColorWithOpacity as defaultDarkenHexColor } from '../utils/color';
import { Clock, MapPin } from 'lucide-react';
import { useMemo } from 'react';

interface WeekViewProps {
  events: Event[];
  currentDate: Date;
  baseUrl?: string;
}

interface DayColumn {
  date: Date;
  isToday: boolean;
  events: Event[];
}

export function WeekView({ events, currentDate, baseUrl = '/events' }: WeekViewProps) {
  const config = useEventsComponents();
  const Link = config.LinkComponent;
  const darkenHexColorWithOpacity = config.darkenHexColorWithOpacity || defaultDarkenHexColor;

  // Build week columns
  const weekDays = useMemo(() => {
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - currentDate.getDay()); // Start on Sunday
    weekStart.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days: DayColumn[] = [];

    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(weekStart);
      dayDate.setDate(weekStart.getDate() + i);

      // Find events for this day
      const dayEvents = events.filter(event => {
        const startTimestamp = event.eventDateTimeRange?.start?.timestamp;
        const endTimestamp = event.eventDateTimeRange?.end?.timestamp;
        if (!startTimestamp || !endTimestamp) return false;

        const eventStart = new Date(startTimestamp * 1000);
        eventStart.setHours(0, 0, 0, 0);

        const eventEnd = new Date(endTimestamp * 1000);
        eventEnd.setHours(23, 59, 59, 999);

        return dayDate >= eventStart && dayDate <= eventEnd;
      });

      // Sort events by time
      dayEvents.sort((a, b) => {
        const aStart = a.eventDateTimeRange?.start?.timestamp || 0;
        const bStart = b.eventDateTimeRange?.start?.timestamp || 0;
        return aStart - bStart;
      });

      days.push({
        date: dayDate,
        isToday: dayDate.getTime() === today.getTime(),
        events: dayEvents,
      });
    }

    return days;
  }, [currentDate, events]);

  return (
    <div className="overflow-x-auto">
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-px bg-border">
        {weekDays.map((day, index) => (
          <div
            key={index}
            className={`bg-card p-3 text-centre ${day.isToday ? 'bg-primary/10' : ''}`}
          >
            <div className="text-xs font-medium text-muted-foreground">
              {day.date.toLocaleDateString('en-GB', { weekday: 'short' })}
            </div>
            <div className={`mt-1 text-lg font-semibold ${day.isToday ? 'text-primary' : ''}`}>
              {day.date.getDate()}
            </div>
          </div>
        ))}
      </div>

      {/* Events grid */}
      <div className="grid grid-cols-7 gap-px bg-border">
        {weekDays.map((day, index) => (
          <div
            key={index}
            className={`min-h-[400px] bg-card p-2 ${day.isToday ? 'bg-primary/5' : ''}`}
          >
            <div className="space-y-2">
              {day.events.map(event => {
                const startTimestamp = event.eventDateTimeRange?.start?.timestamp;
                const startDate = startTimestamp ? new Date(startTimestamp * 1000) : null;
                const startTime = startDate
                  ? startDate.toLocaleTimeString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '';

                // event.path already contains full path like /events/title
                const eventLink = event.path;

                if (!eventLink) return null;

                // Handle eventType - can be single object or array
                const eventTypeArray = Array.isArray(event.eventType)
                  ? event.eventType
                  : event.eventType
                    ? [event.eventType]
                    : [];
                const firstTypeItem = eventTypeArray[0];

                // Only TermEventType has eventTypeColor property
                const eventTypeColor =
                  firstTypeItem && 'eventTypeColor' in firstTypeItem
                    ? (firstTypeItem.eventTypeColor ?? undefined)
                    : undefined;

                const firstType =
                  firstTypeItem && firstTypeItem.id && firstTypeItem.name ? firstTypeItem : null;

                const className = `group block rounded-lg border p-2 transition-colours hover:border-primary hover:shadow-sm cursor-pointer ${
                  eventTypeColor ? '' : 'bg-background'
                }`;
                const style = {
                  backgroundColor: eventTypeColor
                    ? darkenHexColorWithOpacity(eventTypeColor, 80, 0.7)
                    : undefined,
                };

                const content = (
                  <>
                    {startTime && (
                      <div className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" style={{ color: eventTypeColor || undefined }} />
                        <span className="font-medium">{startTime}</span>
                      </div>
                    )}
                    <h4 className="mb-1 line-clamp-2 text-sm font-semibold leading-tight group-hover:text-primary">
                      {event.title}
                    </h4>
                    {event.eventLocation && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{event.eventLocation}</span>
                      </div>
                    )}
                    {firstType && (
                      <div className="mt-1">
                        <span
                          className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium"
                          style={{
                            backgroundColor: eventTypeColor
                              ? `${eventTypeColor}20`
                              : 'hsl(var(--primary) / 0.1)',
                            color: eventTypeColor || 'hsl(var(--primary))',
                          }}
                        >
                          {firstType.name}
                        </span>
                      </div>
                    )}
                  </>
                );

                // Use Link for navigation
                return (
                  <Link key={event.id} href={eventLink || ''} className={className} style={style}>
                    {content}
                  </Link>
                );
              })}
              {day.events.length === 0 && (
                <div className="flex h-full min-h-[100px] items-center justify-centre text-xs text-muted-foreground">
                  No events
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
